// react
import { ChangeEvent, useState } from 'react';

// i18n

// mui
import {
	Alert,
	alpha,
	Box,
	Button,
	Chip,
	Collapse,
	Divider,
	Grid,
	IconButton,
	LinearProgress,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Slider,
	Stack,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	Add as IconAdd,
	Delete as IconDelete,
	MoreVert as IconMoreVert,
	TrendingUp as IconTrending,
} from '@mui/icons-material';

// types
import { ProgressEntry, PlanningActivity } from '../../../@types';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// components
import { CardDetail } from '../CardDetail/CardDetail';

// dialogs
import { DialogActions } from '../../dialogs/DialogActions/DialogActions';

// utils
import { radius } from '../../../utils/designSystem';
import { COLORS, GRADIENTS } from '../../../data';

// endpoints don't use /v1 prefix
const API_URL_V2 =
	process.env.REACT_APP_API_URL?.replace(/\/v1\/?$/, '') || process.env.REACT_APP_API_URL;

// Props

export interface CardPhysicalProgressProps {
	activity: PlanningActivity;
	/** Financial progress (0–100) derived from paid / committed */
	financialProgress?: number;
	onEntryAdded?: () => void;
}

// Component

export const CardPhysicalProgress = ({
	activity,
	financialProgress = 0,
	onEntryAdded,
}: CardPhysicalProgressProps) => {
	const theme = useTheme();

	// auth
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');
	const authHeaders: HeadersInit = {
		...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
		...(idToken ? { User: idToken } : {}),
	};

	// state
	const [entries, setEntries] = useState<ProgressEntry[]>([]);
	const [showForm, setShowForm] = useState(false);
	const [newProgress, setNewProgress] = useState<number>(activity.progress || 0);
	const [newNote, setNewNote] = useState('');
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// kebab menu state
	const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
	const [menuEntryId, setMenuEntryId] = useState<number | null>(null);

	// confirm dialog state
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

	// fetch progress entries
	const { fetchRequest, isLoading: loading } = useFetch({
		url: `${API_URL_V2}/v2/planning-activities/${activity.id}/progress`,
		options: { headers: authHeaders },
		onSuccess: (res) => {
			if (Array.isArray(res?.data)) {
				setEntries(res.data);
			}
		},
	});

	// handlers
	const handleSubmit = async () => {
		setSaving(true);
		setError(null);
		try {
			const res = await fetchRequest(
				`${API_URL_V2}/v2/planning-activities/${activity.id}/progress`,
				{
					method: 'POST',
					headers: { 'Content-Type': 'application/json', ...authHeaders },
					body: JSON.stringify({
						progress: newProgress,
						note: newNote || null,
					}),
				}
			);
			if (res?.data) {
				setEntries((prev) => [res.data, ...prev]);
				setShowForm(false);
				setNewNote('');
				onEntryAdded?.();
			}
		} catch (e) {
			setError((e as Error).message || 'Failed to save');
		} finally {
			setSaving(false);
		}
	};

	const handleDeactivate = async (entryId: number) => {
		try {
			await fetchRequest(
				`${API_URL_V2}/v2/planning-activities/${activity.id}/progress/${entryId}`,
				{
					method: 'PATCH',
					headers: { ...authHeaders, 'Content-Type': 'application/json' },
					body: JSON.stringify({ status: 'DEACTIVATED' }),
				}
			);
			setEntries((prev) => prev.filter((e) => e.id !== entryId));
			onEntryAdded?.();
		} catch {
			// silent
		}
	};

	// kebab menu handlers
	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, entryId: number) => {
		setMenuAnchorEl(event.currentTarget);
		setMenuEntryId(entryId);
	};

	const handleMenuClose = () => {
		setMenuAnchorEl(null);
		setMenuEntryId(null);
	};

	const handleDeleteClick = () => {
		setPendingDeleteId(menuEntryId);
		setShowDeleteDialog(true);
		handleMenuClose();
	};

	const handleConfirmDelete = async () => {
		if (pendingDeleteId !== null) {
			await handleDeactivate(pendingDeleteId);
		}
		setShowDeleteDialog(false);
		setPendingDeleteId(null);
	};

	const handleCancelDelete = () => {
		setShowDeleteDialog(false);
		setPendingDeleteId(null);
	};

	// current physical progress
	const physicalProgress = activity.progress || 0;

	return (
		<CardDetail
			icon={IconTrending}
			title="Progress Update"
			subtitle={`${entries.length} update${entries.length !== 1 ? 's' : ''} recorded`}
			variant="activity"
			themeColor={GRADIENTS.activity.icon}
			headerActions={
				<Tooltip title="Record progress update">
					<IconButton
						size="small"
						onClick={() => setShowForm(!showForm)}
						sx={{
							color: GRADIENTS.activity.icon,
							border: '1px solid',
							borderColor: alpha(GRADIENTS.activity.icon, 0.5),
							borderRadius: '6px',
							padding: '4px',
							'&:hover': {
								bgcolor: alpha(GRADIENTS.activity.icon, 0.08),
								borderColor: GRADIENTS.activity.icon,
							},
						}}>
						<IconAdd sx={{ fontSize: 18 }} />
					</IconButton>
				</Tooltip>
			}>
			{/* Side-by-side progress bars: Physical vs Financial */}
			<Box sx={{ px: 2, py: 1.5 }}>
				<Grid container spacing={2}>
					<Grid item xs={6}>
						<Typography
							sx={{
								color: '#6b7280',
								fontWeight: 600,
								fontSize: '1.25rem',
								mb: 0.5,
							}}>
							Physical
						</Typography>
						<Stack direction="row" alignItems="center" spacing={1}>
							<LinearProgress
								variant="determinate"
								value={physicalProgress}
								sx={{
									flex: 1,
									height: 8,
									borderRadius: 4,
									bgcolor: alpha(theme.palette.primary.main, 0.1),
									'& .MuiLinearProgress-bar': {
										borderRadius: 4,
										bgcolor: theme.palette.primary.main,
									},
								}}
							/>
							<Typography sx={{ fontWeight: 700, fontSize: '1.25rem', minWidth: 36 }}>
								{physicalProgress}%
							</Typography>
						</Stack>
					</Grid>
					<Grid item xs={6}>
						<Typography
							sx={{
								color: '#6b7280',
								fontWeight: 600,
								fontSize: '1.25rem',
								mb: 0.5,
							}}>
							Financial
						</Typography>
						<Stack direction="row" alignItems="center" spacing={1}>
							<LinearProgress
								variant="determinate"
								value={Math.min(financialProgress, 100)}
								sx={{
									flex: 1,
									height: 8,
									borderRadius: 4,
									bgcolor: alpha(COLORS.success.main, 0.1),
									'& .MuiLinearProgress-bar': {
										borderRadius: 4,
										bgcolor: COLORS.success.main,
									},
								}}
							/>
							<Typography sx={{ fontWeight: 700, fontSize: '1.25rem', minWidth: 36 }}>
								{Math.round(financialProgress)}%
							</Typography>
						</Stack>
					</Grid>
				</Grid>
			</Box>

			{/* Add entry form */}
			<Collapse in={showForm}>
				<Box sx={{ px: 2, py: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
					<Stack spacing={1.5}>
						<Typography
							sx={{
								fontWeight: 600,
								color: '#6b7280',
								fontSize: '1.25rem',
							}}>
							New Progress Update
						</Typography>
						<Stack direction="row" alignItems="center" spacing={2}>
							<Typography sx={{ fontWeight: 600, fontSize: '1.25rem', minWidth: 100 }}>
								{newProgress}% complete
							</Typography>
							<Slider
								value={newProgress}
								onChange={(_e, val) => setNewProgress(val as number)}
								min={0}
								max={100}
								step={5}
								size="small"
								sx={{ flex: 1 }}
							/>
						</Stack>
						<TextField
							fullWidth
							label="Note (milestones, deliverables, etc.)"
							multiline
							minRows={2}
							maxRows={4}
							size="small"
							value={newNote}
							onChange={(e: ChangeEvent<HTMLInputElement>) => setNewNote(e.target.value)}
							sx={{
								'& .MuiInputBase-input': { fontSize: '1.25rem' },
								'& .MuiInputLabel-root': { fontSize: '1.25rem' },
							}}
						/>
						{error && (
							<Alert severity="error" sx={{ py: 0, fontSize: '1.25rem' }}>
								{error}
							</Alert>
						)}
						<Stack direction="row" justifyContent="flex-end" spacing={1}>
							<Button size="small" onClick={() => setShowForm(false)} sx={{ fontSize: '1.25rem' }}>
								Cancel
							</Button>
							<Button
								size="small"
								variant="contained"
								onClick={handleSubmit}
								disabled={saving}
								sx={{ fontSize: '1.25rem' }}>
								{saving ? 'Saving…' : 'Save'}
							</Button>
						</Stack>
					</Stack>
				</Box>
			</Collapse>

			<Divider />

			{/* Timeline of entries */}
			<Box sx={{ px: 2, py: 1.5, maxHeight: 300, overflowY: 'auto' }}>
				{loading ? (
					<Typography sx={{ color: '#6b7280', fontSize: '1.25rem' }}>Loading…</Typography>
				) : entries.length === 0 ? (
					<Typography
						sx={{
							color: '#9ca3af',
							fontStyle: 'italic',
							fontSize: '1.35rem',
							textAlign: 'center',
							py: 2,
						}}>
						No progress updates recorded yet
					</Typography>
				) : (
					<Stack spacing={1}>
						{entries.map((entry) => (
							<Stack
								key={entry.id}
								direction="row"
								alignItems="flex-start"
								spacing={1.5}
								sx={{
									p: 1,
									borderRadius: radius.sm,
									'&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
								}}>
								<Box
									sx={{
										width: 8,
										height: 8,
										borderRadius: '50%',
										bgcolor: theme.palette.primary.main,
										mt: 1,
										flexShrink: 0,
									}}
								/>
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Stack direction="row" alignItems="center" spacing={1}>
										<Chip
											label={`${entry.progress}%`}
											size="small"
											sx={{
												fontWeight: 700,
												fontSize: '1rem',
												height: 24,
												bgcolor: alpha(theme.palette.primary.main, 0.1),
												color: theme.palette.primary.main,
											}}
										/>
										<Typography sx={{ color: '#6b7280', fontSize: '1.125rem' }}>
											{new Date(entry.reportedDate).toLocaleDateString('en-US', {
												timeZone: 'UTC',
												month: 'short',
												day: 'numeric',
												year: 'numeric',
											})}
										</Typography>
									</Stack>
									{entry.note && (
										<Typography sx={{ mt: 0.25, color: '#374151', fontSize: '1.25rem' }}>
											{entry.note}
										</Typography>
									)}
								</Box>
								<IconButton
									size="small"
									onClick={(e) => handleMenuOpen(e, entry.id)}
									sx={{ opacity: 0.4, '&:hover': { opacity: 1 } }}>
									<IconMoreVert sx={{ fontSize: 18 }} />
								</IconButton>
							</Stack>
						))}
					</Stack>
				)}
			</Box>

			{/* Kebab menu for entry actions */}
			<Menu
				anchorEl={menuAnchorEl}
				open={Boolean(menuAnchorEl)}
				onClose={handleMenuClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
				<MenuItem onClick={handleDeleteClick} sx={{ color: '#ef4444' }}>
					<ListItemIcon>
						<IconDelete sx={{ fontSize: 18, color: '#ef4444' }} />
					</ListItemIcon>
					<ListItemText>Deactivate</ListItemText>
				</MenuItem>
			</Menu>

			{/* Deactivate entry confirmation dialog */}
			<DialogActions
				isOpen={showDeleteDialog}
				onClose={handleCancelDelete}
				maxWidth={400}
				title={{ children: 'Deactivate Progress Entry' }}
				subtitle={{ children: 'Are you sure? This cannot be undone.' }}
				actions={[
					{
						children: 'Cancel',
						color: 'inherit',
						onClick: handleCancelDelete,
						variant: 'text',
					},
					{
						children: 'Deactivate',
						onClick: handleConfirmDelete,
						variant: 'contained',
						sx: { bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } },
					},
				]}
			/>
		</CardDetail>
	);
};
